/****************************************************
 * Module Name:  BucketDetailModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useState, useEffect } from "react";
import MixBucketProxy from "../../utility/proxy/manager/mixProxy/mixBucketProxy";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import { Box, Button, Tooltip, Avatar, Chip } from "@mui/material";
import renderCellExpand from "../common/dataGridCellExpand";
import Modal from "react-bootstrap/Modal";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import logo from '../../images/pansuriya.jpg'
import CommonSnackbar from "../common/commonSnackBar";

const BucketDetailModal = ({ bucketId, handleClose, show }) => {

    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [detailLoader, setDetailLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);

    useEffect(() => {
        handleSearch(bucketId);
    }, []);

    const handleSearch = (bucketId) => {
        setDetailLoader(true);
        MixBucketProxy.getBucketDetailsByBucketId(bucketId, (responseData) => {
            if (responseData) {
                const uniqueFilterValues = {
                    shape: Utility.getDistinctOptions(responseData, "shape"),
                    size: Utility.getDistinctOptions(responseData, "size"),
                    color: Utility.getDistinctOptions(responseData, "color"),
                    clarity: Utility.getDistinctOptions(responseData, "clarity"),
                    stonetype: Utility.getDistinctOptions(responseData, "stonetypefullname"),
                    username: Utility.getDistinctOptions(responseData, "username"),
                };

                const filterMap = generateFilterMap(Array.isArray(responseData) ? responseData : [responseData]);
                prepareColumns(uniqueFilterValues, filterMap);
                responseData = responseData.sort((a, b) => b.id - a.id);

                let prevRow = null;
                responseData.map((iRow) => {
                    if (prevRow === null) {
                        prevRow = iRow
                        // Considering the 1st row as the previous row, we wont be showing any color change in this
                        iRow.prevRow = iRow;
                    } else {
                        iRow.prevRow = prevRow;
                        prevRow = iRow;
                    }

                    return true;
                });
                setData(responseData);
                setDetailLoader(false);
            } else {
                setDetailLoader(false);
                setErrorSnackbar(true);
            }
        });
    }

    const generateFilterMap = (responseData) => {
        const filterMap = {};

        // Process min alter username
        const minAlterUsername = responseData.flatMap((user) =>
            user.minalertusername.map((u) => ({
                id: u.id,
                username: u.username,
            }))
        );
        filterMap.minalertusername = [
            ...new Set(
                minAlterUsername.map((user) => `${user.id} | ${user.username}`)
            ),
        ];

        // Process max alter username
        const maxAlertUsername = responseData.flatMap((user) =>
            user.maxalertusername.map((u) => ({
                id: u.id,
                username: u.username,
            }))
        );
        filterMap.maxalertusername = [
            ...new Set(
                maxAlertUsername.map(
                    (user) => `${user.id} | ${user.username}`
                )
            ),
        ];

        return filterMap;
    };

    const genericCellValueWithColorClass = (currVal, prevVal) => {
        if (!currVal || (typeof currVal === "string" && currVal.trim() === ''))
            currVal = '';
        if (!prevVal || (typeof prevVal === "string" && prevVal.trim() === ''))
            prevVal = '';

        return currVal !== prevVal ? 'blueCell' : ""
    }

    const prepareColumns = (combinedFilterValues, filterMap) => {
        let columnNames = [];

        Constants.USER_COLUMN_PERMISSION.forEach(iColumnPermission => {
            if (iColumnPermission === "f12c1ec3-2901-4368-ac5f-664720c5600c") {
                columnNames.push(
                    {
                        field: "bucketid",
                        headerName: "BUCKETID",
                        headerAlign: "center",
                        align: "center",
                        type: "number",
                        valueFormatter: (params) => params.value,
                        width: 110,
                    },
                );
            }
        })

        columnNames.push(
            {
                field: "id",
                headerName: "ID",
                headerAlign: "center",
                align: "center",
                type: "number",
                valueFormatter: (params) => params.value,
                width: 80,
                renderCell: renderCellExpand,
            },
            {
                field: "stonetypefullname",
                headerName: "STONETYPE",
                headerAlign: "center",
                align: "center",
                width: 95,
                type: "singleSelect",
                valueOptions: combinedFilterValues ? combinedFilterValues.stonetype : [],
                filterOperators: Utility.combinedFilters(),
                valueGetter: (params) => params.value,
                renderCell: renderCellExpand,
                cellClassName: (params) => genericCellValueWithColorClass(params.row.stonetypefullname, params.row.prevRow.stonetypefullname)
            },
            {
                field: "shape",
                headerName: "SHAPE",
                headerAlign: "center",
                align: "center",
                width: 85,
                type: "singleSelect",
                valueOptions: combinedFilterValues ? combinedFilterValues.shape : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
                valueGetter: (params) => params.value,
                cellClassName: (params) => genericCellValueWithColorClass(params.row.shape, params.row.prevRow.shape)
            },
            {
                field: "size",
                headerName: "SIZE",
                headerAlign: "center",
                align: "right",
                width: 80,
                type: "singleSelect",
                valueOptions: combinedFilterValues ? combinedFilterValues.size : [],
                filterOperators: Utility.combinedFilters(),
                valueGetter: (params) => params.row.size,
                cellClassName: (params) => genericCellValueWithColorClass(params.row.size, params.row.prevRow.size),
                renderCell: renderCellExpand,
            },
            {
                field: "color",
                headerName: "COLOR",
                headerAlign: "center",
                align: "center",
                width: 65,
                type: "singleSelect",
                valueOptions: combinedFilterValues ? combinedFilterValues.color : [],
                filterOperators: Utility.combinedFilters(),
                valueGetter: (params) => params.value,
                cellClassName: (params) => genericCellValueWithColorClass(params.row.color, params.row.prevRow.color),
                renderCell: renderCellExpand,
            },
            {
                field: "clarity",
                headerName: "CLARITY",
                headerAlign: "center",
                align: "center",
                width: 70,
                type: "singleSelect",
                valueOptions: combinedFilterValues ? combinedFilterValues.clarity : [],
                filterOperators: Utility.combinedFilters(),
                valueGetter: (params) => params.value,
                cellClassName: (params) => genericCellValueWithColorClass(params.row.clarity, params.row.prevRow.clarity),
                renderCell: renderCellExpand,
            },
        );

        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                if (columnData.field === "minalertusername") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = filterMap ? filterMap.minalertusername : [];
                    columnData.filterOperators = Utility.customFilters(filterMap, columnData.field, 'id', 'username');
                }
                if (columnData.field === "maxalertusername") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = filterMap ? filterMap.maxalertusername : [];
                    columnData.filterOperators = Utility.customFilters(filterMap, columnData.field, 'id', 'username');
                }
                columnNames.push(columnData);
            }
        });

        columnNames.push(
            {
                field: "qty",
                headerName: "QTY",
                headerAlign: "center",
                align: "center",
                type: "number",
                valueFormatter: (params) => params.value,
                width: 60,
                cellClassName: (params) => genericCellValueWithColorClass(params.row.qty, params.row.prevRow.qty),
                renderCell: renderCellExpand,
            },
            {
                field: "ppc",
                headerName: "PPC",
                headerAlign: "center",
                align: "center",
                type: "number",
                valueFormatter: (params) => params.value,
                width: 85,
                cellClassName: (params) => genericCellValueWithColorClass(params.row.ppc, params.row.prevRow.ppc),
                renderCell: renderCellExpand,
            },
            {
                field: "updatedat",
                headerName: "UPDATED AT",
                headerAlign: "center",
                align: "center",
                width: 95,
                type: "date",
                valueGetter: (params) => {
                    if (params.value) {
                        return new Date(params.value);
                    }
                    return null;
                },
                renderCell: (params) => {
                    if (params.value) {
                        return Utility.ConvertUtcToLocalDate(params.value);
                    } else {
                        return "";
                    }
                },
                cellClassName: (params) => genericCellValueWithColorClass(params.row.updatedat, params.row.prevRow.updatedat),
            },
            {
                field: "username",
                headerName: "USERNAME",
                headerAlign: "center",
                align: "center",
                width: 95,
                type: "singleSelect",
                valueOptions: combinedFilterValues ? combinedFilterValues.username : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
                cellClassName: (params) => genericCellValueWithColorClass(params.row.username, params.row.prevRow.username),
            },
            {
                field: "status",
                headerName: "STATUS",
                headerAlign: "center",
                align: "center",
                width: 112,
                renderCell: renderCellExpand,
                cellClassName: (params) => genericCellValueWithColorClass(params.row.status, params.row.prevRow.status),
            }
        );

        setColumns(columnNames);
    }

    const columnsData = {
        "81ef77f7-d366-447a-a68d-86664f04c8ae": {
            field: "defaultpurchaseprice",
            headerName: "DEFAULT PURCHASE PRICE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 183,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.defaultpurchaseprice, params.row.prevRow.defaultpurchaseprice),
        },
        "3e264059-af48-45e5-917a-fb1b971db4b6": {
            field: "defaultsellprice",
            headerName: "DEFAULT SELL PRICE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 144,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.defaultsellprice, params.row.prevRow.defaultsellprice),
        },
        "45e76150-8506-4e5c-9e00-04812830404b": {
            field: "minalertqty",
            headerName: "MIN ALERT QTY",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 115,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.minalertqty, params.row.prevRow.minalertqty),
            renderCell: renderCellExpand,
        },
        "e38095be-101d-45ed-9a10-aef1aba03f8c": {
            field: "maxalertqty",
            headerName: "MAX ALERT QTY",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 115,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.maxalertqty, params.row.prevRow.maxalertqty),
            renderCell: renderCellExpand,
        },
        "7b076812-4af4-462e-abcb-1eee8f44d740": {
            field: "minalertusername",
            headerName: "MIN ALERT USERNAME",
            headerAlign: "center",
            align: "center",
            width: 160,
            cellClassName: (params) => {
                const currId = Array.isArray(params.row.minalertusername) && params.row.minalertusername.length > 0
                    ? params.row.minalertusername[0].id : '';
                const prevId = Array.isArray(params.row.prevRow.minalertusername) && params.row.prevRow.minalertusername.length > 0
                    ? params.row.prevRow.minalertusername[0].id : '';

                return genericCellValueWithColorClass(currId, prevId);
            },
            valueGetter: (params) => params.value,
            renderCell: (params) => {
                if (params.value.length === 0) {
                    return null;
                }
                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: "10px",
                            rowGap: "10px",
                        }}
                    >
                        {params.value.map((item, index) => (
                            <Chip
                                key={index}
                                avatar={<Avatar sx={{ width: 'auto' }}>{item.id}</Avatar>}
                                label={item.username}
                                sx={{
                                    margin: 0,
                                    maxWidth: "200px",
                                    whiteSpace: "nowrap",
                                }}
                            />
                        ))}
                    </Box>
                );
            },
            disableExport: true,
            sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "username")
        },
        "5a220faa-49a0-403b-8c1a-2e001d7721be": {
            field: "maxalertusername",
            headerName: "MAX ALERT USERNAME",
            headerAlign: "center",
            align: "center",
            width: 160,
            cellClassName: (params) => {
                const currId = Array.isArray(params.row.maxalertusername) && params.row.maxalertusername.length > 0
                    ? params.row.maxalertusername[0].id : '';
                const prevId = Array.isArray(params.row.prevRow.maxalertusername) && params.row.prevRow.maxalertusername.length > 0
                    ? params.row.prevRow.maxalertusername[0].id : '';

                return genericCellValueWithColorClass(currId, prevId);
            },
            valueGetter: (params) => params.value,
            renderCell: (params) => {
                if (params.value.length === 0) {
                    return null;
                }
                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: "10px",
                            rowGap: "10px",
                        }}
                    >
                        {params.value.map((item, index) => (
                            <Chip
                                key={index}
                                avatar={<Avatar sx={{ width: 'auto' }}>{item.id}</Avatar>}
                                label={item.username}
                                sx={{
                                    margin: 0,
                                    maxWidth: "200px",
                                    whiteSpace: "nowrap",
                                }}
                            />
                        ))}
                    </Box>
                );
            },
            disableExport: true,
            sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "username")
        },
        "809a4ec3-4c31-4077-b96d-97d53d57efac": {
            field: "avgprice",
            headerName: "AVG PRICE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 85,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.avgprice, params.row.prevRow.avgprice),
            renderCell: renderCellExpand,
        },
        "03c60e42-31d0-4b12-b8f0-d7e141ae6c28": {
            field: "bucketqty",
            headerName: "BUCKET QTY",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 100,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.bucketqty, params.row.prevRow.bucketqty),
            renderCell: renderCellExpand,
        }
    }

    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />

            <Modal show={show} onHide={handleClose} dialogClassName="bhb" enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Bucket Details&nbsp;
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Box px={1}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "4px 5px",
                                    borderRadius: "4px",
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "5px 5px",
                                    borderRadius: "4px",
                                },
                                "& .blueCell": {
                                    color: "#080808",
                                    backgroundColor: "#d4eff8",
                                },
                                height: "50vh",
                            }}
                        >
                            <DataGridPro
                                loading={detailLoader}
                                disableRowSelectionOnClick={true}
                                getRowHeight={() => "auto"}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "8px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "15px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "22px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight
                                }}
                                density="compact"
                                slots={{ toolbar: GridToolbar }}
                                rows={data}
                                columns={columns}
                                getRowId={(row) => row.id}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            id: false,
                                            minalertusername: false,
                                            maxalertusername: false
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                                {detailLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => handleSearch(bucketId)}
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer", mr: 1 }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                    </Box>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default BucketDetailModal;